/* THE GROUND IS A FIELD, NOT A MOSAIC (LS-218). Vijay: "hide as much of the
 * tile grid in the visuals".
 *
 * A screen's tiles are cut from the world's terrain fields -- the filled
 * height against the sea level, the distance to the coast, the moisture,
 * a little noise -- at TILE CENTRES, so every boundary between water and
 * shallow, shallow and sand, sand and moss, moss and ground ran along cell
 * edges: a staircase, on a world whose cliffs already march the field.
 *
 * This bakes, once per screen, the same cut at PIXEL resolution: the fields
 * sampled between tile centres (bilinear, with the same noise at fractional
 * coordinates) and thresholded by the same rules, so at every tile centre
 * the pixel's kind is the tile's kind and between two centres the boundary
 * lies where the field crosses. Only the SOFT kinds take part -- the six
 * ground covers -- and only where a tile and all eight neighbours are soft:
 * a cliff, a tree, a wall keeps its hard edge (it has its own marching).
 *
 * The overlay repaints every soft tile that has a differing soft neighbour,
 * pixel by pixel, with the texel of the field's kind copied from the nearest
 * UNIFORM tile of that kind on the same screen (so the palette blend and
 * the tile art are the screen's own), and lays a frayed foam line where the
 * field crosses from water to land. The same per-pixel kinds feed the water
 * mask, so the shader's water follows the field too. The tile grid is still
 * the truth for walking: a body on a ground tile may stand with its feet in
 * the last few pixels of a shore that the field drew across the cell, which
 * is what a shore is. */
import { T, TS } from './art.ts';
import { fbm } from './world.ts';

export const SOFT = new Set([T.GROUND, T.MOSS, T.SAND, T.SHALLOW, T.WATER, T.SEA, T.DEBRIS, T.SCORCH, T.BONES, T.ICE, T.FORD, T.THORN]);   // ...and bramble (2026-09-12, Vijay: "art consistency"): a hedge on the shore stood in squares   // debris and scorch are ground with litter on it; their edges meander like the rest
const WETK = new Set([T.SHALLOW, T.WATER, T.SEA]);

/* HOW THE BOUNDARY IS MOVED OFF THE GRID. The first draft re-cut the terrain
 * fields at pixel resolution with the generator's own rules, and disagreed
 * with the tiles wherever a later pass had rewritten them (paths, clearings,
 * mouths): the tiles are the truth and the fields are only where they came
 * from. So the field is derived from the TILE GRID itself. Every pixel asks
 * its own tile and the eight around it; each candidate kind is weighted by a
 * bell around each tile's centre (sigma of six pixels, so at a centre the
 * tile's own kind wins by thirty to one and between two centres the two are
 * level), the weights are wobbled by a continuous noise of the world
 * coordinates (so the level line meanders by a few pixels and never runs
 * straight along a cell edge), and the heaviest kind takes the pixel. That
 * rounds every corner and bends every edge, agrees with every tile at its
 * centre, and needs nothing but the tiles. */
const SIGMA = 10;
/* THE WARP IS MADE ONCE PER SCREEN, ON A LATTICE. Three fields (the ground,
 * the rock, the level) read the same warp; asking the noise four times per
 * pixel per field put a screen's bake at 130 ms, an eight-frame hitch when a
 * neighbour is baked mid-walk. The warp is sampled every four pixels and
 * interpolated -- it is a drift of a few pixels over a couple of tiles and
 * a ripple of one, both far wider than four pixels -- and shared. */
const WARP = 6, LAT = 4;
const warpCache = new Map();
export function warpFor(seed, wx0, wy0, cols, rows) {
  const steps = warpForSteps(seed, wx0, wy0, cols, rows);
  let step = steps.next();
  while (!step.done) step = steps.next();
  return step.value;
}

export function* warpForSteps(seed, wx0, wy0, cols, rows) {
  const key = seed + ':' + wx0 + ',' + wy0 + ':' + cols + 'x' + rows;
  const hit = warpCache.get(key);
  if (hit) { warpCache.delete(key); warpCache.set(key, hit); return hit; }
  const W = cols * TS, H = rows * TS;
  const gw = Math.ceil(W / LAT) + 1, gh = Math.ceil(H / LAT) + 1;
  const gx = new Float32Array(gw * gh), gy = new Float32Array(gw * gh);
  for (let j = 0; j < gh; j++) {
    if (j && j % 32 === 0) yield;
    for (let i = 0; i < gw; i++) {
    const wxp = wx0 + (i * LAT + 0.5) / TS, wyp = wy0 + (j * LAT + 0.5) / TS;
    // a slow drift over three tiles, a quicker one over a tile and a half, and a ripple: a long straight edge has nothing to hold on to
    // the noise sits within about a sixth of its range either side of the middle (measured: a gain of six gave two pixels), so the gain is high
    const G = 1.8;
    gx[j * gw + i] = ((fbm(seed + 313, wxp, wyp, 0.3) - 0.5) * 2 * WARP + (fbm(seed + 323, wxp, wyp, 0.65) - 0.5) * 6 + (fbm(seed + 331, wxp, wyp, 1.7) - 0.5) * 3) * G;
    gy[j * gw + i] = ((fbm(seed + 317, wxp, wyp, 0.3) - 0.5) * 2 * WARP + (fbm(seed + 327, wxp, wyp, 0.65) - 0.5) * 6 + (fbm(seed + 337, wxp, wyp, 1.7) - 0.5) * 3) * G;
    }
  }
  /* EVERY EDGE SEGMENT BULGES. The noise warp is smooth, so along a run of
   * cells it is often the same one or two pixels for a whole cell, and a
   * boundary that is 'the cell edge plus two' is still a ruled line. Each
   * sixteen-pixel edge segment gets its own bulge -- a half sine across the
   * segment, up to four pixels either way, fading out eight pixels from the
   * edge -- so no segment of a boundary can stay parallel to its cell edge
   * for more than a few pixels, and the bulges meet at the corners at zero. */
  const BULGE = 5, REACH = 10;
  const hash = (a, b, c) => { let h = (a * 374761393 + b * 668265263 + c * 2246822519 + seed * 97) | 0; h = (h ^ (h >>> 13)) * 1274126177 | 0; return ((h ^ (h >>> 16)) >>> 0) / 4294967296; };
  const dx = new Float32Array(W * H), dy = new Float32Array(W * H);
  yield;
  for (let py = 0; py < H; py++) {
    if (py && py % 128 === 0) yield;
    const fy = py / LAT, j0 = Math.min(gh - 2, Math.floor(fy)), v = fy - j0;
    const ey = Math.round((py + 0.5) / TS) * TS;                  // the nearest horizontal edge
    const fyE = Math.max(0, 1 - Math.abs(py + 0.5 - ey) / REACH);
    for (let px = 0; px < W; px++) {
      const fx = px / LAT, i0 = Math.min(gw - 2, Math.floor(fx)), u = fx - i0;
      const a = j0 * gw + i0, b = a + 1, c = a + gw, d = c + 1;
      let ddx = (gx[a] * (1 - u) + gx[b] * u) * (1 - v) + (gx[c] * (1 - u) + gx[d] * u) * v;
      let ddy = (gy[a] * (1 - u) + gy[b] * u) * (1 - v) + (gy[c] * (1 - u) + gy[d] * u) * v;
      // a bulge is never flat: at least two pixels, up to five, either way
      const amp = (h) => { const v = h * 2 - 1; return (v < 0 ? -1 : 1) * (2 + (BULGE - 2) * Math.abs(v)); };
      if (fyE > 0) { const tx = Math.floor(px / TS), t = (px + 0.5 - tx * TS) / TS; ddy += amp(hash(wx0 + tx, wy0 + ey / TS, 11)) * Math.sin(Math.PI * t) * fyE; }
      const ex = Math.round((px + 0.5) / TS) * TS;                // the nearest vertical edge
      const fxE = Math.max(0, 1 - Math.abs(px + 0.5 - ex) / REACH);
      if (fxE > 0) { const ty = Math.floor(py / TS), t = (py + 0.5 - ty * TS) / TS; ddx += amp(hash(wx0 + ex / TS, wy0 + ty, 13)) * Math.sin(Math.PI * t) * fxE; }
      // never more than six pixels: the tile is where the game collides, and an outline a half-tile off it is a lie
      dx[py * W + px] = Math.max(-6, Math.min(6, ddx)); dy[py * W + px] = Math.max(-6, Math.min(6, ddy));
    }
  }
  /* THE COAST DRIFTS FURTHER. A shoreline is the one boundary that reads as
   * a ruled line at a two-pixel wobble -- it is long, and nothing crosses it
   * -- so where a wet label meets a dry one the sample drifts by a slower,
   * wider noise on top of the warp: up to ten pixels, over three tiles. The
   * tile centre still keeps its own label, so a bay that reaches into a sand
   * cell leaves a sandbar rather than drowning the cell. */
  const cx = new Float32Array(W * H), cy = new Float32Array(W * H);
  {
    const gcx = new Float32Array(gw * gh), gcy = new Float32Array(gw * gh);
    yield;
    for (let j = 0; j < gh; j++) {
      if (j && j % 32 === 0) yield;
      for (let i = 0; i < gw; i++) {
      const wxp = wx0 + (i * LAT + 0.5) / TS, wyp = wy0 + (j * LAT + 0.5) / TS;
      gcx[j * gw + i] = (fbm(seed + 353, wxp, wyp, 0.12) - 0.5) * 2 * 10 * 2.5;
      gcy[j * gw + i] = (fbm(seed + 359, wxp, wyp, 0.12) - 0.5) * 2 * 10 * 2.5;
      }
    }
    yield;
    for (let py = 0; py < H; py++) {
      if (py && py % 128 === 0) yield;
      const fy = py / LAT, j0 = Math.min(gh - 2, Math.floor(fy)), v = fy - j0;
      for (let px = 0; px < W; px++) {
        const fx = px / LAT, i0 = Math.min(gw - 2, Math.floor(fx)), u = fx - i0;
        const a = j0 * gw + i0, b = a + 1, c = a + gw, d = c + 1;
        cx[py * W + px] = Math.max(-10, Math.min(10, (gcx[a] * (1 - u) + gcx[b] * u) * (1 - v) + (gcx[c] * (1 - u) + gcx[d] * u) * v));
        cy[py * W + px] = Math.max(-10, Math.min(10, (gcy[a] * (1 - u) + gcy[b] * u) * (1 - v) + (gcy[c] * (1 - u) + gcy[d] * u) * v));
      }
    }
  }
  const made = { dx, dy, cx, cy };
  if (!warpCache.has(key) && warpCache.size >= 64) warpCache.delete(warpCache.keys().next().value);
  warpCache.set(key, made);
  return made;
}   // a bell that rounds a corner over half a tile, a meander of up to eight pixels; the four centre pixels of a tile are always its own
/* `bias` is the extra weight a cliff or stair label carries: the ground field
 * needs it (1.35) or the turf's soil eats the low cliffs; the rock field must
 * not have it, or its outline is pushed a uniform two or three pixels out of
 * the cell and the cell's edge stays as a floor under the meander. `coast`
 * turns on the wider drift where a wet label meets a dry one (LS-218). */
/* SMOOTHING A SCREEN'S LABELS, A FEW ROWS AT A TIME (stepped 2026-09-06).
 *
 * This is the costliest thing in a screen's bake and it was one uninterruptible call. Measured: the `canopy` phase
 * was 19.4 ms of a screen's 72, and 15.3 ms of THAT was this function, called once from `paintMass` over the padded
 * grid -- 448x384 rather than a screen's 256x192, which is three and a half times the pixels. So the pre-bake could
 * be paced as finely as you liked and one step still blew the frame, because one step contained this.
 *
 * It is a generator now, yielding every 48 rows of output, and `smoothLabels` below drains it for the callers that
 * want it in one go (`fieldKinds`, and the pals/levels/front phases, all of which are already inside budget). The
 * body is untouched apart from the yield: the same warp, the same weights, the same centre pull, in the same order,
 * so every pixel it writes is the pixel it wrote before. */
const SMOOTH_ROWS = 32;   // measured: a screen becomes 34 steps whose costliest is 11-12 ms, which leaves room for the frame own ~4 ms inside the 16.7 ms budget. 48 left steps of 16-17 ms, which did not; 16 cut the median further but made a screen 58 steps for no gain in the worst.
export function* smoothLabelsSteps(labelAt, cols, rows, seed, wx0, wy0, allowed = null, bias = 1.35, coast = false) {
  const W = cols * TS, H = rows * TS;
  const out = new Uint16Array(W * H);   // labels are kinds, levels, or palette ids (biome pair, mix and level), which run past 255
  const inv = 1 / (2 * SIGMA * SIGMA);
  const warp = yield* warpForSteps(seed, wx0, wy0, cols, rows);
  /* THE LABELS ARE READ ONCE. `labelAt` is a closure into the world, and it
   * was asked nine times for every pixel; a screen's five fields put a bake at
   * a quarter of a second on the desk, which is a walk that stutters on a
   * phone. The labels of the screen and one tile beyond it go into a grid,
   * with -1 for none and -2 for a label the field may not move. */
  const GC = cols + 2, GR = rows + 2;
  const L = new Int32Array(GC * GR);
  for (let ty = -1; ty <= rows; ty++) for (let tx = -1; tx <= cols; tx++) {
    const k = labelAt(tx, ty);
    L[(ty + 1) * GC + tx + 1] = (k === null || k === undefined) ? -1 : (allowed && !allowed(k)) ? -2 : k;
  }
  const lab = (tx, ty) => (tx < -1 || ty < -1 || tx > cols || ty > rows) ? -1 : L[(ty + 1) * GC + tx + 1];
  // Shore proximity is a tile property, not a per-pixel neighbourhood query.
  const shore = coast ? new Uint8Array(cols * rows) : null;
  if (shore) for (let ty = 0; ty < rows; ty++) for (let tx = 0; tx < cols; tx++) {
    const wet = WETK.has(lab(tx, ty));
    let near = false;
    for (let dy = -1; dy <= 1 && !near; dy++) for (let dx = -1; dx <= 1; dx++) {
      const k = lab(tx + dx, ty + dy);
      if (k >= 0 && WETK.has(k) !== wet) { near = true; break; }
    }
    shore[ty * cols + tx] = near ? 1 : 0;
  }
  // a tile whose eight neighbours all carry its own label needs no vote: most of a screen
  const UNI = new Uint8Array(GC * GR);
  for (let ty = -1; ty <= rows; ty++) for (let tx = -1; tx <= cols; tx++) {
    const own = lab(tx, ty); let u = own >= 0 ? 1 : 0;
    for (let dy = -1; dy <= 1 && u; dy++) for (let dx = -1; dx <= 1; dx++) { const k = lab(tx + dx, ty + dy); if (k >= 0 && k !== own) { u = 0; break; } }
    UNI[(ty + 1) * GC + tx + 1] = u;
  }
  // exp(-d2 / 2 sigma^2) from a table: d2 never passes two tiles squared
  const EXPT = new Float32Array(1300);
  for (let i = 0; i < 1300; i++) EXPT[i] = Math.exp(-i * inv);
  const wts = new Map();
  for (let py = 0; py < H; py++) {
    if (py && py % SMOOTH_ROWS === 0) yield;
    for (let px = 0; px < W; px++) {
      const tx0 = Math.floor(px / TS), ty0 = Math.floor(py / TS);
      const own0 = lab(tx0, ty0);
      if (own0 === -2) { out[py * W + px] = labelAt(tx0, ty0); continue; }
      if (Math.abs(px % TS - 7.5) <= 1 && Math.abs(py % TS - 7.5) <= 1) { out[py * W + px] = own0; continue; }
      let sx = px + 0.5 + warp.dx[py * W + px];
      let sy = py + 0.5 + warp.dy[py * W + px];
      if (shore && shore[ty0 * cols + tx0]) {
        sx += warp.cx[py * W + px]; sy += warp.cy[py * W + px];
      }
      const tx = Math.floor(sx / TS), ty = Math.floor(sy / TS);
      const own = lab(tx, ty);
      if (own < 0) { out[py * W + px] = own0; continue; }
      if (UNI[(ty + 1) * GC + tx + 1]) { out[py * W + px] = own; continue; }
      wts.clear();
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const nx = tx + dx, ny = ty + dy;
        const k = lab(nx, ny);
        if (k < 0) continue;
        const cx = nx * TS + TS / 2, cy = ny * TS + TS / 2;
        const d2 = (sx - cx) * (sx - cx) + (sy - cy) * (sy - cy);
        const w = EXPT[Math.min(1299, d2 | 0)] * (k === T.CLIFF ? bias : 1);
        wts.set(k, (wts.get(k) || 0) + w);
      }
      // THE CENTRE PULLS. The four snapped pixels keep the contract; around
      // them the tile's own label gets a bonus that fades out five pixels from
      // the centre, so a warp that carries a boundary deep into a tile bends
      // it round the centre instead of cutting an island out of it.
      const dcx = px % TS - 7.5, dcy = py % TS - 7.5;
      const dc = Math.sqrt(dcx * dcx + dcy * dcy);
      if (dc < 5.5) wts.set(own0, (wts.get(own0) || 0) + 0.7 * (1 - dc / 5.5));
      let best = own, bw = -1;
      for (const [k, w] of wts) if (w > bw) { bw = w; best = k; }
      out[py * W + px] = best;
    }
  }
  return out;
}

/** The same field, in one go, for every caller that is already inside its budget. */
export function smoothLabels(labelAt, cols, rows, seed, wx0, wy0, allowed = null, bias = 1.35, coast = false) {
  const it = smoothLabelsSteps(labelAt, cols, rows, seed, wx0, wy0, allowed, bias, coast);
  let r = it.next();
  while (!r.done) r = it.next();
  return r.value;
}

/** Per-pixel kinds for a wild screen: the soft ground kinds smoothed off the grid, the hard kinds as their cells. */
export const SMOOTH = new Set([...SOFT, T.CLIFF]);   // the low cliff's edge meanders with the ground it stands in
export function* fieldKindsSteps(world, tiles, cols, rows, sx, sy, beyond = null, bias = 1.35) {
  const seed = world ? (world.seedNum || 0) : 0;
  // beyond(tx, ty) answers for a tile outside this screen (the neighbour's), so the field has no seam
  /* A NEIGHBOUR'S CLIFF STOPS AT THE SEAM (2026-09-12; Vijay's coast shot, "that seam at the top"). Screen 3,10 of
   * route-1 has a wall of cliff along its top row; the label smoothing let that cliff wander into the sand of the
   * screen above, whose bake painted the strayed pixels with the only cliff texel it had -- a dark crag face -- so a
   * dark band ran along the screen's foot with a ruled edge where the two bakes met. A cliff beyond the edge is
   * ground to this screen unless this screen's own edge tile is cliff too (a wall that crosses the seam continues).
   * And a cliff that ends at the seam keeps its outline two pixels inside it, so the outline meanders (2 to 6 px in)
   * rather than lying on the screen's edge where the meander happens to be nil. */
  const inside = (tx, ty) => tx >= 0 && ty >= 0 && tx < cols && ty < rows;
  const raw = (tx, ty) => inside(tx, ty) ? tiles[ty][tx] : beyond ? beyond(tx, ty) : null;
  const labelAt = (tx, ty) => {
    if (inside(tx, ty)) return tiles[ty][tx];
    const v = raw(tx, ty);
    if (v === T.CLIFF && tiles[Math.min(Math.max(ty, 0), rows - 1)][Math.min(Math.max(tx, 0), cols - 1)] !== T.CLIFF) return T.GROUND;
    return v;
  };
  const out = yield* smoothLabelsSteps(labelAt, cols, rows, seed, sx * cols, sy * rows, (k) => SMOOTH.has(k), bias, bias !== 1);   // the ground field (biased) has coasts; the rock field has none
  if (beyond) {
    const W = cols * TS, H = rows * TS, INSET = 2;
    for (let py = 0; py < H; py++) for (let px = 0; px < W; px++) {
      if (Math.min(px, py, W - 1 - px, H - 1 - py) >= INSET || out[py * W + px] !== T.CLIFF) continue;
      const tx = Math.floor(px / TS), ty = Math.floor(py / TS);
      if (tiles[ty][tx] !== T.CLIFF) continue;
      const bx = px < INSET ? -1 : px >= W - INSET ? cols : tx, by = py < INSET ? -1 : py >= H - INSET ? rows : ty;
      const v = raw(bx, by);
      if (v === null || v === undefined || v === T.CLIFF) continue;
      out[py * W + px] = SMOOTH.has(v) ? v : T.GROUND;
    }
  }
  return out;
}

/** The same, in one go. */
export function fieldKinds(world, tiles, cols, rows, sx, sy, beyond = null, bias = 1.35) {
  const it = fieldKindsSteps(world, tiles, cols, rows, sx, sy, beyond, bias);
  let r = it.next();
  while (!r.done) r = it.next();
  return r.value;
}

/** The overlay canvas: repaints the boundary tiles from the pixel kinds, texels copied from the screen's own tile layer. */
export function bakeGroundField(tileLayer, tiles, cols, rows, kinds, seed = 0, wx0 = 0, wy0 = 0, rock = null, levels = null, levelOf = null, palOf = null) {
  const steps = bakeGroundFieldSteps(tileLayer, tiles, cols, rows, kinds, seed, wx0, wy0, rock, levels, levelOf, palOf);
  let result = steps.next();
  while (!result.done) result = steps.next();
  return result.value;
}

export function* bakeGroundFieldSteps(tileLayer, tiles, cols, rows, kinds, seed = 0, wx0 = 0, wy0 = 0, rock = null, levels = null, levelOf = null, palOf = null, synth = null) {
  /* A KIND THIS SCREEN HAS NO TILE OF IS PAINTED FRESH (2026-09-12; Vijay's coast shot, "that seam at the top"). The
   * texels come from the screen's own tile layer, so a label that named a kind at a level the screen has no tile of --
   * the higher terrace's sand wandering over the seam into the beach below it -- fell back to the nearest level and
   * painted nothing new, and the terrace's tone changed on the ruled line where the two bakes met. `synth(kind,
   * label)` answers with a fresh uniform tile of that kind in that label's palette (the game paints it with the tile
   * painter), so the wander is painted on both sides of the seam. */
  const synthCache = new Map();
  const synthOf = (k, lb) => { const key = k + '|' + lb; if (synthCache.has(key)) return synthCache.get(key); let sd = null; try { const im = synth ? synth(k, lb) : null; sd = im && im.data ? im.data : null; } catch (e) { sd = null; } synthCache.set(key, sd); return sd; };
  /* THE LEVEL IS PART OF THE KIND. Ground on a higher terrace is painted in a
   * darker or lighter palette (terracePal), so two ground tiles a step apart
   * met on a ruled line though both were 'ground'; a texel source is chosen
   * by kind AND level, and a pixel whose level field disagrees with its tile
   * is repainted like one whose kind does (LS-218). The rock field, where
   * given, does the same for the cliff's own outline: a cliff tile's pixels
   * beyond the meander take the ground beside them, and ground pixels inside
   * it take the rock's. */
  const lvOf0 = (tx, ty) => (levelOf ? levelOf(tx, ty) : 0) | 0;
  const LV = new Int32Array(cols * rows);   // the tile levels once: `levelOf` is a closure into the world and was asked per pixel
  for (let ty = 0; ty < rows; ty++) for (let tx = 0; tx < cols; tx++) LV[ty * cols + tx] = lvOf0(tx, ty);
  const lvOf = (tx, ty) => (tx >= 0 && ty >= 0 && tx < cols && ty < rows) ? LV[ty * cols + tx] : lvOf0(tx, ty);
  if (!kinds || !tileLayer) return null;
  const W = cols * TS, H = rows * TS;
  const src = tileLayer.getContext('2d');
  const img = src.getImageData && src.getImageData(0, 0, W, H);
  if (!img || !img.data) return null;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  const out = g.createImageData && g.createImageData(W, H);
  if (!out || !out.data) return null;
  const d = img.data, o = out.data;
  // uniform tiles of each kind: the texel sources
  const uniform = new Map();   // kind -> [[tx, ty], ...]
  const anyOf = new Map();
  const same = (tx, ty, k, lv) => tx >= 0 && ty >= 0 && tx < cols && ty < rows && tiles[ty][tx] === k && lvOf(tx, ty) === lv;
  const PAINT = (k) => SOFT.has(k) || k === T.CLIFF;
  for (let ty = 0; ty < rows; ty++) for (let tx = 0; tx < cols; tx++) {
    const k = tiles[ty][tx];
    if (!PAINT(k)) continue;
    const lv = lvOf(tx, ty), kk = k + '|' + lv;
    if (!anyOf.has(kk)) anyOf.set(kk, []);
    anyOf.get(kk).push([tx, ty]);
    let uni = true;
    for (let dy = -1; dy <= 1 && uni; dy++) for (let dx = -1; dx <= 1; dx++) if (!same(tx + dx, ty + dy, k, lv) && !(tx + dx < 0 || ty + dy < 0 || tx + dx >= cols || ty + dy >= rows)) { uni = false; break; }
    if (uni) { if (!uniform.has(kk)) uniform.set(kk, []); uniform.get(kk).push([tx, ty]); }
  }
  const nearest = (list, tx, ty) => { let best = null, bd = 1e9; for (const p of list) { const dd = (p[0] - tx) * (p[0] - tx) + (p[1] - ty) * (p[1] - ty); if (dd < bd) { bd = dd; best = p; } } return best; };
  // Palette labels can exceed 16; packing them into the kind's numeric slot aliases other kinds.
  const texelTile = new Map();
  const sourceFor = (k, lv, tx, ty) => {
    let byKind = texelTile.get(k);
    if (!byKind) { byKind = new Map(); texelTile.set(k, byKind); }
    const key = (lv * cols + tx) * rows + ty;
    let s = byKind.get(key);
    if (s === undefined) {
      const kk = k + '|' + lv;
      s = nearest(uniform.get(kk) || anyOf.get(kk) || [], tx, ty) || null;
      if (!s) { // no tile of that kind at that level on this screen: any level of the kind, rather than nothing
        let best = null, bd = 1e9;
        for (const [key2, list] of anyOf) { if (key2.split('|')[0] !== String(k)) continue; const n = nearest(uniform.get(key2) || list, tx, ty); if (n) { const dd = (n[0] - tx) ** 2 + (n[1] - ty) ** 2; if (dd < bd) { bd = dd; best = n; } } }
        s = best;
      }
      byKind.set(key, s);
    }
    return s;
  };
  // what is repainted: a pixel whose field kind differs from its tile's, and the rim band of a tile's side
  // that faces a different soft kind (the tile art draws its rim there, and a rim on the cell edge IS the grid);
  // everything else keeps the tile's own art, variants and all, so the texture stays varied
  const sides = new Uint8Array(cols * rows);   // bit 1 N, 2 E, 4 S, 8 W
  for (let ty = 0; ty < rows; ty++) for (let tx = 0; tx < cols; tx++) {
    const k = tiles[ty][tx];
    if (!SOFT.has(k)) continue;
    const other = (nx, ny) => nx >= 0 && ny >= 0 && nx < cols && ny < rows && tiles[ny][nx] !== k && SOFT.has(tiles[ny][nx]);
    sides[ty * cols + tx] = (other(tx, ty - 1) ? 1 : 0) | (other(tx + 1, ty) ? 2 : 0) | (other(tx, ty + 1) ? 4 : 0) | (other(tx - 1, ty) ? 8 : 0)
      | (other(tx + 1, ty - 1) || other(tx - 1, ty - 1) ? 1 : 0) | (other(tx + 1, ty + 1) || other(tx - 1, ty + 1) ? 4 : 0);
  }
  const RIMS = { [T.WATER]: 6, [T.SHALLOW]: 6, [T.SEA]: 6 };   // a shoreline's rim is wider than a moss patch's
  const rimOf = (k) => RIMS[k] || 3;
  let painted = 0;
  for (let py = 0; py < H; py++) {
    if (py % 96 === 0) yield;
    const ty = Math.floor(py / TS);
    for (let px = 0; px < W; px++) {
      const tx = Math.floor(px / TS);
      const kt = tiles[ty][tx];
      let k = kinds[py * W + px];
      // the rock field owns the cliff's outline: inside it a pixel is rock, outside it a cliff tile's pixel is the ground the field names there
      if (rock) { const rk = rock[py * W + px]; if (rk === T.CLIFF) k = T.CLIFF; else if (k === T.CLIFF) k = SOFT.has(kt) ? kt : (SOFT.has(kinds[py * W + px]) ? kinds[py * W + px] : k); }
      const lvT = lvOf(tx, ty);
      const lvP = levels ? levels[py * W + px] | 0 : lvT;
      const sd = sides[ty * cols + tx], ix = px % TS, iy = py % TS;
      const RIM = rimOf(kt);
      const inRim = ((sd & 1) && iy < RIM) || ((sd & 4) && iy >= TS - RIM) || ((sd & 8) && ix < RIM) || ((sd & 2) && ix >= TS - RIM);
      if (!inRim && k === kt && lvP === lvT) continue;
      if (!PAINT(k) || !PAINT(kt)) continue;   // a wall, a tree, a floor: keeps its art
      if (k === T.CLIFF && kt === T.CLIFF) continue;   // rock stays its own art; the runs and ledges shape it
      let si;
      if (k === kt && lvP === lvT) {
        // a rim pixel of the tile's own kind: the tile's own art, mirrored inward past the rim, so the texture runs on seamlessly
        const mx = (sd & 8) && ix < RIM ? RIM * 2 - 1 - ix : (sd & 2) && ix >= TS - RIM ? 2 * (TS - RIM) - 1 - ix : ix;
        const my = (sd & 1) && iy < RIM ? RIM * 2 - 1 - iy : (sd & 4) && iy >= TS - RIM ? 2 * (TS - RIM) - 1 - iy : iy;
        si = ((ty * TS + my) * W + (tx * TS + mx)) * 4;
      } else {
        // the tile beside us of the wanted kind, mirrored across the shared edge, so the art runs on without a seam; else the nearest uniform tile
        let mirrored = false;
        for (const [dx, dy] of [[ix >= TS / 2 ? 1 : -1, 0], [0, iy >= TS / 2 ? 1 : -1], [ix >= TS / 2 ? -1 : 1, 0], [0, iy >= TS / 2 ? -1 : 1]]) {
          const nx = tx + dx, ny = ty + dy;
          if (nx < 0 || ny < 0 || nx >= cols || ny >= rows || tiles[ny][nx] !== k || (k !== T.CLIFF && lvOf(nx, ny) !== lvP)) continue;   // rock is rock at any level
          // mirrored across the shared edge, but from inside the neighbour's own rim band: the tile art draws
          // its shoreline or margin on that side, and a mirror of the rim put a second shoreline in the meander
          const rn = RIMS[k] || 0;   // only the wet kinds draw a rim in their art; the rest mirror exactly, or the join itself is a seam
          const mx = dx ? Math.min(TS - 1, rn + (TS - 1 - ix)) : ix, my = dy ? Math.min(TS - 1, rn + (TS - 1 - iy)) : iy;
          si = ((ny * TS + my) * W + (nx * TS + mx)) * 4; mirrored = true; break;
        }
        if (!mirrored) {
          const exact = uniform.has(k + '|' + lvP) || anyOf.has(k + '|' + lvP);
          let s = exact ? sourceFor(k, lvP, tx, ty) : null;
          if (!s && synth && k !== T.CLIFF && k !== T.THORN) {   // a bramble's picture is a prop over the ground, not a texel
            const sd = synthOf(k, lvP);
            if (sd) { const j = ((py % TS) * TS + (px % TS)) * 4, oi2 = (py * W + px) * 4; o[oi2] = sd[j]; o[oi2 + 1] = sd[j + 1]; o[oi2 + 2] = sd[j + 2]; o[oi2 + 3] = 255; painted++; continue; }
          }
          if (!s) s = sourceFor(k, lvP, tx, ty);
          if (!s) continue;
          si = ((s[1] * TS + (py % TS)) * W + (s[0] * TS + (px % TS))) * 4;
        }
      }
      const oi = (py * W + px) * 4;
      o[oi] = d[si]; o[oi + 1] = d[si + 1]; o[oi + 2] = d[si + 2]; o[oi + 3] = 255;
      painted++;
    }
  }
  /* THE CLIFF'S EDGE MEANDERS (LS-218). A low cliff's own art -- a lip and a
   * face, oriented by the land -- cannot be repainted from texels the way the
   * soft ground is, and it is the loudest straight line on a screen: a step of
   * one terrace runs along cell edges for tiles on end. So a cliff tile that
   * meets soft ground above or below has each pixel COLUMN shifted up or
   * down by a few pixels of continuous noise (the lip and the face move
   * together, so the art keeps its structure), and the strip that shifting
   * uncovers is filled from the ground beside it; a tile that meets ground
   * to its left or right has its rows shifted the same way. The noise is a
   * function of world position, so the meander is continuous from tile to
   * tile and the same every time the screen is baked. */
  {
    const seed2 = seed || 0;
    const wob = (u, salt) => Math.max(-4, Math.min(4, Math.round((fbm(seed2 + salt, u, 0.5, 0.35) - 0.5) * 2 * 6.5)));   // the noise sits within about a third of its range either side of the middle, so the gain is high: two to four pixels most of the way
    const isSoft = (tx, ty) => tx >= 0 && ty >= 0 && tx < cols && ty < rows && SOFT.has(tiles[ty][tx]);
    const src4 = (x, y) => ((y * W) + x) * 4;
    const put = (x, y, si) => { if (x < 0 || y < 0 || x >= W || y >= H) return; const oi = src4(x, y); o[oi] = d[si]; o[oi + 1] = d[si + 1]; o[oi + 2] = d[si + 2]; o[oi + 3] = 255; };
    // the shift of a column is read off the label field: where, along the column, the field's cliff begins
    const rockK = rock || kinds;   // the rock field knows every cliff cell; the ground field calls a side cliff ground
    const cliffAt = (x, y) => x >= 0 && y >= 0 && x < W && y < H && rockK[y * W + x] === T.CLIFF;
    const shiftN = (px, ty) => { const top = ty * TS; let y = top - 4; while (y < top + 4 && !cliffAt(px, y)) y++; return Math.max(-4, Math.min(4, y - top)); };
    const shiftW = (py, tx) => { const left = tx * TS; let x = left - 4; while (x < left + 4 && !cliffAt(x, py)) x++; return Math.max(-4, Math.min(4, x - left)); };
    for (let ty = 0; ty < rows; ty++) for (let tx = 0; tx < cols; tx++) {
      if (tiles[ty][tx] !== T.CLIFF) continue;
      const softN = isSoft(tx, ty - 1), softS = isSoft(tx, ty + 1), softW = isSoft(tx - 1, ty), softE = isSoft(tx + 1, ty);
      if (softN || softS) {
        for (let x = 0; x < TS; x++) {
          const px = tx * TS + x;
          const dd = softN ? shiftN(px, ty) : wob(wx0 + tx + (x + 0.5) / TS, 17);
          // the tile's column, shifted by dd; the ground strip it leaves is filled from the neighbour
          for (let y = 0; y < TS; y++) put(px, ty * TS + y + dd, src4(px, ty * TS + y));
          if (dd > 0 && softN) for (let y = 0; y < dd; y++) { const gy = (ty - 1) * TS + TS - 1 - (y % TS); put(px, ty * TS + y, src4(px, gy)); }
          if (dd < 0 && softS) for (let y = TS + dd; y < TS; y++) { const gy = (ty + 1) * TS + (y - (TS + dd)); put(px, ty * TS + y, src4(px, Math.min(H - 1, gy))); }
        }
      } else if (softW || softE) {
        for (let y = 0; y < TS; y++) {
          const py = ty * TS + y;
          const dd = softW ? shiftW(py, tx) : wob(wy0 + ty + (y + 0.5) / TS, 23);
          for (let x = 0; x < TS; x++) put(tx * TS + x + dd, py, src4(tx * TS + x, py));
          if (dd > 0 && softW) for (let x = 0; x < dd; x++) { const gx = (tx - 1) * TS + TS - 1 - (x % TS); put(tx * TS + x, py, src4(gx, py)); }
          if (dd < 0 && softE) for (let x = TS + dd; x < TS; x++) { const gx = (tx + 1) * TS + (x - (TS + dd)); put(tx * TS + x, py, src4(Math.min(W - 1, gx), py)); }
        }
      }
    }
  }
  yield;
  /* THE SHORELINE FOAM, ON THE WATER SIDE OF THE MEANDER. The tile art drew
   * its foam on the cell's edge, and the rim repaint above rightly took it
   * away; what it left was a faint pale line on the land, which read as no
   * shore at all -- "we need to fix the shoreline foam". A wet pixel one step
   * from land is foam (the biome's own foam colour, nearly solid, frayed by a
   * hash), two steps is half foam, three and four are the shallows' tint
   * fading out; so the foam follows the meander the field drew. */
  {
    const REACH = 40;   // the shelf: the tile art shelved the sea in three cell-wide steps; here it is one slope, forty pixels wide
    const wet = new Uint8Array(W * H);
    for (let i = 0; i < W * H; i++) wet[i] = WETK.has(kinds[i]) ? 1 : 0;
    // the steps from land for every wet pixel, by a two-pass chamfer (a forward sweep and a backward one) rather than forty dilations
    const BIG = 255;
    const dist = new Uint8Array(W * H);
    for (let py = 0; py < H; py++) for (let px = 0; px < W; px++) {
      const i = py * W + px;
      if (!wet[i]) { dist[i] = 0; continue; }
      let d0 = BIG;
      if (px > 0) d0 = Math.min(d0, wet[i - 1] ? dist[i - 1] + 1 : 1);
      if (py > 0) d0 = Math.min(d0, wet[i - W] ? dist[i - W] + 1 : 1);
      if (px === 0 || py === 0) d0 = Math.min(d0, BIG);
      dist[i] = d0;
    }
    for (let py = H - 1; py >= 0; py--) for (let px = W - 1; px >= 0; px--) {
      const i = py * W + px;
      if (!wet[i]) continue;
      let d0 = dist[i];
      if (px < W - 1) d0 = Math.min(d0, wet[i + 1] ? dist[i + 1] + 1 : 1);
      if (py < H - 1) d0 = Math.min(d0, wet[i + W] ? dist[i + W] + 1 : 1);
      dist[i] = d0;
    }
    yield;
    for (let i = 0; i < W * H; i++) if (dist[i] > REACH) dist[i] = 0;   // open water, out of the shelf's reach
    const hex = (c) => [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
    const palCache = new Map();
    const tileColours = new Array(cols * rows);
    const colours = (tx, ty) => {
      const index = ty * cols + tx;
      if (tileColours[index]) return tileColours[index];
      const pal = palOf ? palOf(tx, ty) : null;
      const key = pal ? (pal.foam || '') + (pal.shallow || '') : '';
      let c = palCache.get(key);
      if (!c) { c = { foam: hex(pal && pal.foam ? pal.foam : '#b0dce4'), shallow: hex(pal && pal.shallow ? pal.shallow : '#6aa8b6') }; palCache.set(key, c); }
      return tileColours[index] = c;
    };
    for (let py = 0; py < H; py++) for (let px = 0; px < W; px++) {
      const i = py * W + px, dd = dist[i];
      if (!dd) continue;
      let hsh = (px * 374761393 + py * 668265263 + seed * 97) | 0; hsh = (hsh ^ (hsh >>> 13)) * 1274126177 | 0;
      const r = ((hsh ^ (hsh >>> 16)) >>> 0) / 4294967296;
      // foam at one and two steps; then the shelf: the shallows' tint, strongest at the foam and gone forty pixels out
      const mix = dd === 1 ? (r < 0.85 ? 0.9 : 0) : dd === 2 ? (r < 0.6 ? 0.55 : 0.4) : 0.62 * (1 - (dd - 2) / (REACH - 2));
      if (!mix) continue;
      const oi = i * 4;
      const base = o[oi + 3] ? o : d;
      const c = colours(Math.floor(px / TS), Math.floor(py / TS));
      const tint = dd <= 2 ? c.foam : c.shallow;
      o[oi] = Math.round(base[oi] * (1 - mix) + tint[0] * mix); o[oi + 1] = Math.round(base[oi + 1] * (1 - mix) + tint[1] * mix); o[oi + 2] = Math.round(base[oi + 2] * (1 - mix) + tint[2] * mix); o[oi + 3] = 255;
      painted++;
    }
  }
  if (!painted) { let anyO = false; for (let i = 3; i < o.length; i += 4) if (o[i]) { anyO = true; break; } if (!anyO) return null; }
  g.putImageData(out, 0, 0);
  return c;
}
